import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import Stepper from 'react-stepper-horizontal';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

//components
import withLocalization from '../../../../hoc/withLocalization';
import Button from '../../../../components/CustomButton/CustomButton.jsx';

//assets
import CloseModalIcon from '../../../../assets/img/CloseApproveModalIcon.svg';

//local
import { FutureHoldConflictBody } from './FutureHoldConflictModal';
import { StepOne, StepTwo, StepThree, StepFour } from './HoldProjectRegisterSteps';

const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';
const shiftDate = (date, days) => moment(date).add(days, 'days').format('YYYY-MM-DD');


const getInitialState = () => ({
    activeStep: 0,
    projectId: null,
    projectName: null,
    groupId: null,
    groupMemberIds: [],
    memberIds: [],
    fromDate: null,
    toDate: null,
    reason: '',
    notifyUser: false,
    affectedRosters: [],
    loadingRosters: false,
    vacationConflictUserIds: [],
    affectedWorkPatterns: [],
    loadingWorkPatterns: false,
    confirming: false,
    futureHoldConflicts: null,
    revertingConflicts: false,
});

class RegisterHoldProjectModal extends Component {
    constructor(props) {
        super(props);
        this.state = getInitialState();
    }

    componentDidMount() {
        const { scheduleStore } = this.props;
        if (!scheduleStore.resources) {
            scheduleStore.loadResources({}, true, false);
        }
    }

    getSteps = () => {
        const { t } = this.props;
        return [
            { title: t('Hold Details') },
            { title: t('Affected Templates') },
            { title: t('Affected Assignments') },
            { title: t('Review & Confirm') },
        ];
    };

    getDateFormat = () => this.props.dateFormat || DEFAULT_DATE_FORMAT;

    getTotalDays = () => {
        const { fromDate, toDate } = this.state;
        if (!fromDate || !toDate) return 0;
        const diff = moment(toDate).startOf('day').diff(moment(fromDate).startOf('day'), 'days') + 1;
        return diff > 0 ? diff : 0;
    };

    getResources = () => (this.props.scheduleStore.resources || []).filter(r => !!r.id);

    getSelectedMembers = () => {
        const { memberIds } = this.state;
        return this.getResources().filter(m => memberIds.includes(m.id));
    };

    hasVacationConflict = userId => this.state.vacationConflictUserIds.includes(userId);

    getConflictingMembers = () => this.getSelectedMembers().filter(m => this.hasVacationConflict(m.id));

    // one row per unique template amongst the selected members, sourced from
    // the roster data fetched in handleNext() when leaving Step 1
    getAffectedTemplates = () => {
        const byTemplate = {};
        this.state.affectedRosters.forEach(r => {
            if (!byTemplate[r.roster_template_id]) {
                byTemplate[r.roster_template_id] = {
                    template_id: r.roster_template_id,
                    template_name: r.template_name,
                    shared: r.is_shared,
                    other_assignees: r.other_assignees_count,
                    members: [],
                };
            }
            byTemplate[r.roster_template_id].members.push({ id: r.user_id, name: r.user_name });
        });
        return Object.values(byTemplate);
    };

    getAffectedAssignments = () => {
        const totalDays = this.getTotalDays();
        return this.state.affectedRosters.map(r => ({
            id: r.user_id,
            name: r.user_name,
            template_id: r.roster_template_id,
            template_name: r.template_name,
            shared: r.is_shared,
            cycle_start: r.from_date,
            cycle_end: r.to_date,
            new_start: shiftDate(r.from_date, totalDays),
            new_end: shiftDate(r.to_date, totalDays),
            hasVacationConflict: this.hasVacationConflict(r.user_id),
        }));
    };

    canGoNext = () => {
        const { activeStep, memberIds, fromDate, toDate } = this.state;
        if (activeStep === 0) {
            return !!memberIds.length && !!fromDate && !!toDate
                && moment(toDate).startOf('day').isSameOrAfter(moment(fromDate).startOf('day'));
        }
        return true;
    };

    handleNext = () => {
        const { commonStore, vacationStore, t } = this.props;
        const { biztype } = commonStore.config;
        if (!this.canGoNext()) {
            return commonStore?.addNotification?.(t('Please fill in all required fields'), null, 'error');
        }

        if (this.state.activeStep === 0) {
            const { fromDate, toDate, projectId } = this.state;

            if (!projectId && biztype.data && biztype.data.allow_multiple_branches) {
                return commonStore.addNotification(t("Please select Project ( Branch ) and then try again"), null, 'warning');
            }
            this.setState({ loadingRosters: true });
            return vacationStore.getHoldProjectRosters(this.state.memberIds, moment(fromDate).format('YYYY-MM-DD'), moment(toDate).format('YYYY-MM-DD'))
                .then(({ rows, vacationConflictUserIds }) => this.setState(prev => ({
                    activeStep: prev.activeStep + 1,
                    affectedRosters: rows,
                    vacationConflictUserIds,
                    loadingRosters: false,
                })))
                .catch(err => {
                    this.setState({ loadingRosters: false });
                    commonStore?.addNotification?.(err?.response?.body?.error || t('Something went wrong'), null, 'error');
                });
        }

        if (this.state.activeStep === 1) {
            const workPatternAssignmentIds = [...new Set(
                this.state.affectedRosters.map(r => r.work_pattern_assignment_id).filter(id => !!id)
            )];
            if (!workPatternAssignmentIds.length) {
                return this.setState(prev => ({ activeStep: prev.activeStep + 1, affectedWorkPatterns: [] }));
            }
            this.setState({ loadingWorkPatterns: true });
            return vacationStore.getHoldProjectWorkPatternAssignments(workPatternAssignmentIds)
                .then(rows => this.setState(prev => ({ activeStep: prev.activeStep + 1, affectedWorkPatterns: rows, loadingWorkPatterns: false })))
                .catch(err => {
                    this.setState({ loadingWorkPatterns: false });
                    commonStore?.addNotification?.(err?.response?.body?.error || t('Something went wrong'), null, 'error');
                });
        }

        this.setState(prev => ({ activeStep: prev.activeStep + 1 }));
    };

    handleBack = () => this.setState(prev => ({ activeStep: prev.activeStep - 1 }));

    // Project is informational context for the hold, not a filter - it
    // doesn't scope which members can be selected.
    handleProjectChange = (projectId, full) => {
        this.setState({ projectId, projectName: full ? full.label : null });
    };

    handleGroupChange = groupId => {
        const { commonStore, userGroupStore, t } = this.props;
        if (!groupId || groupId === -1) return this.setState({ groupId: null, groupMemberIds: [] });
        return userGroupStore.getUsersInGroup(groupId)
            .then(({ user_groups }) => {
                const resourceIds = new Set(this.getResources().map(r => r.id));
                const matchedMemberIds = (user_groups || [])
                    .map(row => row.user_id)
                    .filter(id => resourceIds.has(id));
                this.setState({ groupId, groupMemberIds: matchedMemberIds, memberIds: matchedMemberIds });
            })
            .catch(err => {
                commonStore?.addNotification?.(err?.response?.body?.error || t('Something went wrong'), null, 'error');
            });
    };

    handleMembersChange = memberIds => this.setState({ memberIds });

    handleConfirm = () => {
        const { commonStore, vacationStore, t, onRegister } = this.props;
        const { projectId, projectName, memberIds, fromDate, toDate, reason } = this.state;
        const selectedMembers = this.getSelectedMembers();
        const fromDateStr = moment(fromDate).format('YYYY-MM-DD');
        const toDateStr = moment(toDate).format('YYYY-MM-DD');

        this.setState({ confirming: true });
        vacationStore.applyHoldProject({
            member_ids: memberIds,
            from_date: fromDateStr,
            to_date: toDateStr,
            project_id: projectId,
            reason,
        })
            .then(res => {
                onRegister?.({
                    id: res.id,
                    project_id: projectId,
                    project_name: projectName,
                    member_names: selectedMembers.map(m => m.name),
                    from_date: fromDateStr,
                    to_date: toDateStr,
                    total_days: res.total_days,
                    reason,
                });

                let message = `${t('Project hold registered')}. ${res.migrated_count} ${t('affected assignment(s) were shifted forward by')} ${res.total_days} ${t('days')}.`;
                if (res.skipped_member_names?.length) {
                    message += ` ${res.skipped_member_names.length} ${t('employee(s) have a Work Pattern Assignment that is not active and were not migrated')}: ${res.skipped_member_names.join(', ')}.`;
                }
                if (res.vacation_conflict_member_names?.length) {
                    message += ` ${res.vacation_conflict_member_names.length} ${t('employee(s) already have a vacation or absence registered during this period and were not included')}: ${res.vacation_conflict_member_names.join(', ')}.`;
                }
                const hasWarnings = !!(res.skipped_member_names?.length || res.vacation_conflict_member_names?.length);
                commonStore?.addNotification?.(message, null, hasWarnings ? 'warning' : 'success');
                this.setState({ confirming: false });
                this.handleClose();
            })
            .catch(err => {
                this.setState({ confirming: false });
                const body = err?.response?.body;
                if (body?.hasFutureHoldConflict) {
                    return this.setState({ futureHoldConflicts: body.conflicts || [] });
                }
                commonStore?.addNotification?.(body?.error || t('Something went wrong'), null, 'error');
            });
    };

    handleRevertConflictsAndContinue = () => {
        const { commonStore, vacationStore, t } = this.props;
        const { futureHoldConflicts } = this.state;

        this.setState({ revertingConflicts: true });
        Promise.all(futureHoldConflicts.map(c => vacationStore.revertHoldProject(c.meta_data_id)))
            .then(() => {
                this.setState({ revertingConflicts: false, futureHoldConflicts: null });
                this.handleConfirm();
            })
            .catch(err => {
                this.setState({ revertingConflicts: false });
                commonStore?.addNotification?.(err?.response?.body?.error || t('Something went wrong'), null, 'error');
            });
    };

    handleClose = () => {
        this.setState(getInitialState());
        this.props.onHide();
    };

    getSummaryRows = () => {
        const { t } = this.props;
        const dateFormat = this.getDateFormat();
        const { projectName, fromDate, toDate, reason } = this.state;
        const selectedMembers = this.getSelectedMembers();
        const templates = this.getAffectedTemplates();
        const forkCount = templates.filter(tpl => tpl.shared).length;
        const updateCount = templates.length - forkCount;

        const rows = [
            { label: t('Project'), value: projectName },
            {
                label: t('Members'), value: selectedMembers.map(m => (
                    this.hasVacationConflict(m.id) ? `${m.name} (${t('excluded')})` : m.name
                )).join(', ')
            },
            { label: t('Hold Period'), value: `${moment(fromDate).format(dateFormat)} → ${moment(toDate).format(dateFormat)}` },
            { label: t('Total days'), value: this.getTotalDays() },
            { label: t('Templates to update'), value: updateCount },
            { label: t('New assignment versions'), value: forkCount },
        ];
        if (reason) rows.push({ label: t('Reason'), value: reason });
        return rows;
    };

    render() {
        const { show, t } = this.props;
        const { activeStep, loadingRosters, loadingWorkPatterns, confirming, futureHoldConflicts, revertingConflicts } = this.state;
        const isLoadingNext = loadingRosters || loadingWorkPatterns;
        if (!show) return null;

        const hasConflict = !!futureHoldConflicts;

        return (
            <Modal centered backdrop="static" size="lg" show={show} onHide={this.handleClose} className="hold-project-modal">
                <Modal.Header>
                    <Modal.Title className="fs14 fw-700">
                        {hasConflict ? t('Future Hold Already Registered') : t('Register Paused / Hold Project')}
                    </Modal.Title>
                    <img src={CloseModalIcon} className="cursor-pointer" alt="close icon" onClick={this.handleClose} />
                </Modal.Header>
                {!hasConflict && (
                    <div className="stepContainer stepper_progress">
                        <Stepper steps={this.getSteps()} activeStep={activeStep} className="StepClass" />
                    </div>
                )}
                <Modal.Body>
                    {hasConflict ? (
                        <FutureHoldConflictBody conflicts={futureHoldConflicts} dateFormat={this.getDateFormat()} t={t} />
                    ) : (
                        <>
                            {activeStep === 0 && (
                                <StepOne
                                    t={t}
                                    dateFormat={this.getDateFormat()}
                                    resources={this.getResources()}
                                    projectId={this.state.projectId}
                                    onProjectChange={this.handleProjectChange}
                                    groupId={this.state.groupId}
                                    groupMemberIds={this.state.groupMemberIds}
                                    onGroupChange={this.handleGroupChange}
                                    memberIds={this.state.memberIds}
                                    onMembersChange={this.handleMembersChange}
                                    fromDate={this.state.fromDate}
                                    toDate={this.state.toDate}
                                    onFromDateChange={date => this.setState({ fromDate: date, toDate: this.state.toDate && date && moment(this.state.toDate).isBefore(date) ? null : this.state.toDate })}
                                    onToDateChange={date => this.setState({ toDate: date })}
                                    reason={this.state.reason}
                                    onReasonChange={e => this.setState({ reason: e.target ? e.target.value : e })}
                                    totalDays={this.getTotalDays()}
                                />
                            )}
                            {activeStep === 1 && (
                                <StepTwo
                                    t={t}
                                    templates={this.getAffectedTemplates()}
                                    conflictingMembers={this.getConflictingMembers()}
                                    hasVacationConflict={this.hasVacationConflict}
                                />
                            )}
                            {activeStep === 2 && (
                                <StepThree
                                    t={t}
                                    dateFormat={this.getDateFormat()}
                                    assignments={this.getAffectedAssignments()}
                                    affectedWorkPatterns={this.state.affectedWorkPatterns}
                                    resources={this.getResources()}
                                    fromDate={this.state.fromDate}
                                    toDate={this.state.toDate}
                                    totalDays={this.getTotalDays()}
                                />
                            )}
                            {activeStep === 3 && (
                                <StepFour
                                    t={t}
                                    notifyUser={this.state.notifyUser}
                                    onNotifyUserChange={checked => this.setState({ notifyUser: checked })}
                                    conflictingMembers={this.getConflictingMembers()}
                                    summaryRows={this.getSummaryRows()}
                                />
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {hasConflict ? (
                        <>
                            <Button fill danger disabled={revertingConflicts} onClick={this.handleRevertConflictsAndContinue}>
                                {revertingConflicts ? t('Reverting') : t('Revert & Continue')}
                            </Button>
                            <Button onClick={() => this.setState({ futureHoldConflicts: null })} disabled={revertingConflicts}>{t('Cancel')}</Button>
                        </>
                    ) : (
                        <>
                            {activeStep > 0 && <Button onClick={this.handleBack} disabled={isLoadingNext || confirming}>{t('Back')}</Button>}
                            <Button onClick={this.handleClose} disabled={confirming}>{t('Cancel')}</Button>
                            {activeStep < 3
                                ? <Button fill onClick={this.handleNext} disabled={isLoadingNext}>{isLoadingNext ? t('Loading') : t('Next')}</Button>
                                : <Button fill onClick={this.handleConfirm} disabled={confirming}>{confirming ? t('Applying') : t('Confirm & Apply')}</Button>}
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        );
    }
}

export default inject('commonStore', 'vacationStore', 'scheduleStore', 'userGroupStore')(withLocalization(observer(RegisterHoldProjectModal)));
